import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut,
  User,
  Menu,
  X,
  Home,
  ChevronDown,
  Bell,
  Users,
  GraduationCap,
  Briefcase,
  Database,
  ShieldCheck,
  Building2,
  KeyRound,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { REACT_APP_LOGO_URL } from "../constants";
import useAuth from "../contexts/AuthContext";


interface NavButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  active: boolean;
}

/* ─── Helpers ───────────────────────────────────────────────────── */
const NavButton = ({ onClick, children, active }: NavButtonProps) => (
  <button
    onClick={onClick}
    className={`relative px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center gap-1.5 group
      ${active
        ? "text-blue-700 bg-blue-50"
        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
      }`}
  >
    {children}
    {active && (
      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-blue-600" />
    )}
  </button>
);

/* ─── Main Component ────────────────────────────────────────────── */
const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout, isAdmin, isSuperAdmin, isRecruitmentAdmin, isStudentAdmin, isApplicant } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuDropdownRef = useRef<HTMLDivElement>(null);

  /* Scroll shadow */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Close admin dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(e.target as Node)) {
        setAdminOpen(false);
      }
      if (userMenuDropdownRef.current && !userMenuDropdownRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Close mobile menu on route change */
  useEffect(() => {
    setMenuOpen(false);
    setAdminOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;
  const adminLinks = [
    { label: "Notices", path: "/admin/notices", icon: Bell },
  ];

  if (isStudentAdmin) {
    adminLinks.push({ label: "Student Vacancies", path: "/admin/admission-vacancies", icon: GraduationCap });
  }

  if (isRecruitmentAdmin) {
    adminLinks.push(
      { label: "Faculty Vacancies", path: "/faculty/admin/recruitment/all", icon: Users },
      { label: "Non-Faculty Vacancies", path: "/non-faculty/admin/recruitment/all", icon: Briefcase },
      { label: "Master Data", path: "/admin/master-data", icon: Database }
    );
  }

  if (isSuperAdmin) {
    adminLinks.push({ label: "User Management", path: "/admin/users", icon: Users });
  }

  const isAdminActive = adminLinks.some((l) => location.pathname.startsWith(l.path));

  const go = (path: string) => {
    navigate(path);
    setMenuOpen(false);
    setAdminOpen(false);
    setUserMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? "shadow-lg" : "shadow-sm border-b border-slate-100"
        }`}
    >
      {/* ── Accent stripe ──────────────────────────────────────────── */}
      <div className="h-1 w-full bg-gradient-to-r from-blue-700 via-blue-500 to-amber-500" />

      {/* ── Main bar ───────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">

        {/* LEFT: Brand */}
        <motion.div
          className="flex items-center gap-3 cursor-pointer flex-shrink-0 group"
          onClick={() => go("/")}
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.15 }}
        >
          {/* Logo */}
          <div className="relative h-11 w-11 flex-shrink-0">
            <div className="h-full w-full rounded-full ring-2 ring-slate-200 ring-offset-1 overflow-hidden bg-white flex items-center justify-center shadow-sm">
              <img
                src={REACT_APP_LOGO_URL}
                alt="AIIMS Mangalagiri Logo"
                className="h-full w-full object-contain p-0.5"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "https://via.placeholder.com/150?text=A";
                }}
              />
            </div>
            {/* Online indicator */}
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-400 ring-2 ring-white" />
          </div>

          {/* Divider */}
          <div className="h-9 w-px bg-slate-200" />

          {/* Text */}
          <div className="flex flex-col leading-none">
            <h1 className="font-serif font-bold text-base sm:text-lg text-slate-900 tracking-tight group-hover:text-blue-800 transition-colors duration-200">
              AIIMS Mangalagiri
            </h1>
            <p className="text-[9px] sm:text-[10px] text-amber-600 font-bold tracking-[0.2em] uppercase mt-0.5">
              Excellence in Healthcare
            </p>
          </div>
        </motion.div>

        {/* RIGHT: Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          <NavButton onClick={() => go("/")} active={isActive("/")}>
            <Home className="h-3.5 w-3.5" />
            Home
          </NavButton>
          <NavButton onClick={() => go("/allied")} active={isActive("/allied")}>
            <GraduationCap className="h-3.5 w-3.5" />
            Admissions
          </NavButton>
          <NavButton onClick={() => go("/faculty")} active={isActive("/faculty")}>
            <User className="h-3.5 w-3.5" />
            Faculty
          </NavButton>
          <NavButton onClick={() => go("/non-faculty")} active={isActive("/non-faculty")}>
            <User className="h-3.5 w-3.5" />
            Non-Faculty
          </NavButton>
          {isAuthenticated && !isApplicant && (
            <NavButton onClick={() => go("/quarters")} active={location.pathname.startsWith("/quarters")}>
              <Building2 className="h-3.5 w-3.5" />
              Quarters
            </NavButton>
          )}
          {isAuthenticated ? (
            <>
              {/* Admin dropdown */}
              {isAdmin && (
                <div className="relative" ref={adminDropdownRef}>
                  <button
                    onClick={() => setAdminOpen((v) => !v)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200
                      ${isAdminActive || adminOpen
                        ? "text-blue-700 bg-blue-50"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                      }`}
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Admin Panel
                    <motion.span
                      animate={{ rotate: adminOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="inline-flex"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </motion.span>
                  </button>

                  <AnimatePresence>
                    {adminOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 overflow-hidden"
                        style={{ transformOrigin: "top right" }}
                      >
                        <p className="px-3 pt-1 pb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                          Management
                        </p>
                        {adminLinks.map(({ label, path, icon: Icon }) => (
                          <button
                            key={path}
                            onClick={() => go(path)}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-150
                              ${location.pathname.startsWith(path)
                                ? "bg-blue-50 text-blue-700 font-medium"
                                : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                              }`}
                          >
                            <Icon className="h-4 w-4 flex-shrink-0 opacity-60" />
                            {label}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Divider */}
              <div className="h-6 w-px bg-slate-200 mx-1" />

              {/* User Dropdown Menu */}
              <div className="relative" ref={userMenuDropdownRef}>
                <button
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-full transition-all duration-200 focus:outline-none"
                >
                  <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-3.5 w-3.5 text-blue-600" />
                  </div>
                  <span className="text-sm text-slate-700 font-medium max-w-[120px] truncate">
                    {user?.name || "User"}
                  </span>
                  <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.97 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-1.5 overflow-hidden"
                      style={{ transformOrigin: "top right" }}
                    >
                      <div className="px-3 py-2 border-b border-slate-100">
                        <p className="text-xs font-semibold text-slate-900 truncate">
                          {user?.name || "User"}
                        </p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {user?.email || ""}
                        </p>
                        <span className="inline-block mt-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 rounded">
                          {user?.role || "applicant"}
                        </span>
                      </div>
                      
                      <div className="py-1">
                        <button
                          onClick={() => go("/change-password")}
                          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors duration-150"
                        >
                          <KeyRound className="h-4 w-4 text-slate-500" />
                          Change Password
                        </button>
                      </div>

                      <div className="border-t border-slate-100 pt-1">
                        <button
                          onClick={logout}
                          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                        >
                          <LogOut className="h-4 w-4 text-red-500" />
                          Logout
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 ml-2 pl-3 border-l border-slate-200">
              <button
                onClick={() => go("/login")}
                className="px-4 py-1.5 text-sm font-medium text-slate-700 hover:text-slate-900 rounded-md hover:bg-slate-100 transition-colors"
              >
                Login
              </button>
              <button
                onClick={() => go("/register")}
                className="px-5 py-1.5 text-sm font-semibold text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition-colors shadow-sm shadow-blue-200"
              >
                Register
              </button>
            </div>
          )}
        </nav>

        {/* Mobile hamburger */}
        <motion.button
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
          whileTap={{ scale: 0.9 }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {menuOpen ? (
              <motion.span
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="block"
              >
                <X className="h-5 w-5" />
              </motion.span>
            ) : (
              <motion.span
                key="open"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="block"
              >
                <Menu className="h-5 w-5" />
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      {/* ── Mobile menu ────────────────────────────────────────────── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="md:hidden overflow-hidden border-t border-slate-100"
          >
            <div className="bg-white px-4 py-3 space-y-1 pb-5">
              {/* User info banner */}
              {isAuthenticated && (
                <div className="flex items-center gap-3 px-3 py-2.5 mb-2 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">{user?.name || "User"}</p>
                    {isAdmin && (
                      <p className="text-xs text-blue-600 font-medium">Administrator</p>
                    )}
                  </div>
                </div>
              )}

              {/* Home */}
              <MobileNavItem
                icon={<Home className="h-4 w-4" />}
                label="Home"
                onClick={() => go("/")}
                active={isActive("/")}
              />
              <MobileNavItem
                icon={<GraduationCap className="h-4 w-4" />}
                label="Admissions"
                onClick={() => go("/allied")}
                active={isActive("/allied")}
              />
              <MobileNavItem
                icon={<User className="h-4 w-4" />}
                label="Faculty"
                onClick={() => go("/faculty")}
                active={isActive("/faculty")}
              />
              <MobileNavItem
                icon={<User className="h-4 w-4" />}
                label="Non-Faculty"
                onClick={() => go("/non-faculty")}
                active={isActive("/non-faculty")}
              />
              {isAuthenticated && !isApplicant && (
                <MobileNavItem
                  icon={<Building2 className="h-4 w-4" />}
                  label="Quarters"
                  onClick={() => go("/quarters")}
                  active={location.pathname.startsWith("/quarters")}
                />
              )}
              {isAuthenticated && (
                <MobileNavItem
                  icon={<KeyRound className="h-4 w-4" />}
                  label="Change Password"
                  onClick={() => go("/change-password")}
                  active={isActive("/change-password")}
                />
              )}

              {/* Admin links */}
              {isAuthenticated && isAdmin && (
                <>
                  <p className="px-3 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Admin Panel
                  </p>
                  {adminLinks.map(({ label, path, icon: Icon }) => (
                    <MobileNavItem
                      key={path}
                      icon={<Icon className="h-4 w-4" />}
                      label={label}
                      onClick={() => go(path)}
                      active={location.pathname.startsWith(path)}
                    />
                  ))}
                </>
              )}

              {/* Auth actions */}
              <div className="pt-3 mt-1 border-t border-slate-100">
                {isAuthenticated ? (
                  <button
                    onClick={() => { logout(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-xl border border-red-100 hover:bg-red-100 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => go("/login")}
                      className="w-full py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
                    >
                      Login
                    </button>
                    <button
                      onClick={() => go("/register")}
                      className="w-full py-2.5 text-sm font-semibold text-white bg-blue-700 rounded-xl hover:bg-blue-800 transition-colors shadow-sm"
                    >
                      Register
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

interface MobileNavItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active: boolean;
}

/* ─── Mobile Nav Item ───────────────────────────────────────────── */
const MobileNavItem = ({ icon, label, onClick, active }: MobileNavItemProps) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-colors duration-150
      ${active
        ? "bg-blue-50 text-blue-700 border border-blue-100"
        : "text-slate-700 hover:bg-slate-100"
      }`}
  >
    <span className={active ? "text-blue-600" : "text-slate-400"}>{icon}</span>
    {label}
  </button>
);

export default Header;
